'use client';

import { Fragment, useEffect, useState, type ElementType } from 'react';
import { cva } from 'class-variance-authority';
import { LogOut, UserIcon } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { getUserMenuItems } from '@/constants/user-menu';
import { useAuth } from '@/contexts/auth-context';
import { getUserInitials } from '@/lib/utils';

import { LogoutDialog } from './auth/logout-dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Skeleton } from './ui/skeleton';

const menuItemVariants = cva(
  'group/item flex min-w-44 cursor-pointer items-center gap-2 rounded-md p-1.5 transition-colors [&_svg]:size-5',
  {
    variants: {
      variant: {
        normal: 'hover:bg-brand-soft/25 focus:bg-brand-soft/25 text-gray-900',
        destructive:
          'text-red-600 hover:bg-red-50 focus:bg-red-50 hover:text-red-600 focus:text-red-600',
      },
    },
    defaultVariants: {
      variant: 'normal',
    },
  },
);

const menuIconVariants = cva(
  'flex size-8 items-center justify-center rounded-full transition-colors',
  {
    variants: {
      variant: {
        normal:
          '[&_svg]:text-gray-500 group-hover/item:[&_svg]:text-brand-green group-focus/item:[&_svg]:text-brand-green [&_svg]:size-5',
        destructive:
          '[&_svg]:text-red-600 group-hover/item:[&_svg]:text-red-600 group-focus/item:[&_svg]:text-red-600 [&_svg]:size-5',
      },
    },
    defaultVariants: {
      variant: 'normal',
    },
  },
);

const UserMenuItemIcon = ({
  icon: Icon,
  variant = 'normal',
}: {
  icon: ElementType;
  variant?: 'normal' | 'destructive';
}) => (
  <div className={menuIconVariants({ variant })}>
    <Icon />
  </div>
);

const UserMenuSkeleton = () => <Skeleton className='ml-1 size-10 rounded-xl' />;

type UserMenuProps = {
  className?: string;
};

export default function UserMenu({ className }: UserMenuProps) {
  const { user, isLoading, error } = useAuth();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  if (isLoading) return <UserMenuSkeleton />;
  if (error || !user) return <UserMenuSkeleton />;

  const initials = user.fullName && getUserInitials(user.fullName, 2);

  const handleLogout = () => {
    setOpen(false);
  };

  const invertAdmin = pathname.startsWith('/admin');
  const menuItems = getUserMenuItems(user.username, user.role, invertAdmin);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger className={className}>
        <Avatar
          key={user.profilePicture}
          className='rounded-full border border-gray-200 bg-white shadow-sm transition-all hover:bg-gray-50'
        >
          <AvatarImage
            src={user.profilePicture}
            referrerPolicy='no-referrer'
            className='rounded-full'
          />
          <AvatarFallback className='rounded-full bg-transparent text-sm font-medium'>
            {initials ? initials : <UserIcon className='size-4' />}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='min-w-[220px] rounded-lg border border-gray-200 bg-white p-0'
        sideOffset={8}
      >
        <div className='flex flex-col items-start gap-0.5 border-b border-gray-100 p-3'>
          <div className='flex max-w-[200px] flex-col'>
            <span className='block truncate text-sm font-semibold leading-tight text-gray-900'>
              {user.fullName}
            </span>
            <span className='block truncate text-xs font-normal text-gray-500'>
              {user.email}
            </span>
          </div>
        </div>
        <div className='py-0.5'>
          {menuItems.map(({ label, href, icon: Icon }, index) => (
            <Fragment key={`${label}-${index}`}>
              <DropdownMenuItem className={menuItemVariants()} asChild>
                <Link href={href}>
                  <UserMenuItemIcon icon={Icon} />
                  <span className='text-xs font-medium'>{label}</span>
                </Link>
              </DropdownMenuItem>
              {index < menuItems.length - 1 && (
                <div className='mx-3 my-0.5 h-px bg-gray-100' />
              )}
            </Fragment>
          ))}
        </div>
        <div>
          <div className='mx-3 my-0.5 h-px bg-gray-100' />
          <LogoutDialog onLogout={handleLogout}>
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              className={menuItemVariants({ variant: 'destructive' })}
            >
              <UserMenuItemIcon icon={LogOut} variant='destructive' />
              <span className='text-xs font-medium'>Log Out</span>
            </DropdownMenuItem>
          </LogoutDialog>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
